import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faStore, faThumbtack, faUser } from "@fortawesome/free-solid-svg-icons";
import { LateTag, PriorityChip } from "../../crmpostventa-shared/components/Chips";
import {
  EMPTY,
  codeOf,
  formatCuit,
  formatDate,
  formatDuration,
  formatRelativeDays,
  formatTime,
  labelFor,
  toIsoDate,
} from "../../crmpostventa-shared/crmFormat";
import {
  ACTORES,
  CANAL_LABELS,
  ESTADO_LABELS,
  ESTADO_TONES,
  MOTIVO_CIERRE_LABELS,
  ORIGEN_LABELS,
  TIPO_LABELS,
  daysBetween,
  isMine,
  isProactiva,
  motivoLabel,
} from "../ipcFormat";

// Cell contents shared by the table and the mobile cards: a main line and, below it, a caption in ink-muted.
// Missing data is written with words or a dash, never an empty cell.

export const Muted = ({ children = EMPTY }) => <span className="crm-rollo__muted">{children}</span>;

const NewTag = ({ children }) => (children ? <span className="crm-rollo__new-tag">{children}</span> : null);

export function ComercioCell({ item, badge = null }) {
  const numero = item.numeroComercio ? ` · N° ${item.numeroComercio}` : "";
  return (
    <>
      <span className="crm-rollo__t">{item.nombreComercio || "Comercio sin nombre"}</span>
      <NewTag>{badge}</NewTag>
      <span className="crm-rollo__s">{`CUIT ${formatCuit(item.cuit)}${numero}`}</span>
    </>
  );
}

// Type chip: outlined, dashed for Proactiva. An IPC histórico without type is "Tipo sin dato" in muted text, never
// Proactiva (Prompt 6, one style for the missing type).
export function TipoChip({ item }) {
  if (isProactiva(item)) return <span className="crm-ipc__type crm-ipc__type--proactiva">Proactiva</span>;
  const tipo = codeOf(item?.tipo);
  if (!tipo) return <Muted>Tipo sin dato</Muted>;
  return <span className="crm-ipc__type">{TIPO_LABELS[tipo] || item.tipo}</span>;
}

// Origin chip only for gestiones not created by an advisor (Zoco Check, Sin facturación).
export function OrigenChip({ origen }) {
  const code = codeOf(origen);
  if (!code || code === "ASESOR") return null;
  return <span className="crm-rollo__chip crm-rollo__chip--origen">{labelFor(ORIGEN_LABELS, origen)}</span>;
}

export function MotivoCell({ item }) {
  return (
    <>
      {motivoLabel(item)}
      <OrigenChip origen={item.origen} />
    </>
  );
}

export const canalLabel = (canal) => (canal ? labelFor(CANAL_LABELS, canal) : null);

export function CanalCell({ item }) {
  const label = canalLabel(item.canal);
  return label ? <>{label}</> : <Muted />;
}

// "Histórico" chip, only the first letter uppercase (Prompt 6).
export const HistoricoChip = () => <span className="crm-ipc__hist">Histórico</span>;

// StatusChip of a state of the module; a histórico keeps its original text in a neutral outlined chip.
export function IpcStatusChip({ item }) {
  if (item.esHistorico) {
    return (
      <span className="crm-rollo__chip crm-rollo__chip--status crm-ipc__chip--legacy" title="Estado original en el sistema anterior">
        {String(item.estado || "").trim() || "Sin estado"}
      </span>
    );
  }
  const tone = ESTADO_TONES[codeOf(item.estado)] || "discarded";
  return <span className={`crm-rollo__chip crm-rollo__chip--status crm-ipc__chip--${tone}`}>{labelFor(ESTADO_LABELS, item.estado)}</span>;
}

const ACTOR_VIEW = {
  asesor: { icon: faUser, before: "Le toca al ", who: "asesor" },
  comercio: { icon: faStore, before: "Le toca al ", who: "comercio" },
  area: { icon: faBuilding, before: "Le toca a ", who: "otra área" },
};

// Who has to act on an open gestión.
export function Actor({ estado }) {
  const view = ACTOR_VIEW[ACTORES[codeOf(estado)]];
  if (!view) return null;
  return (
    <span className="crm-ipc__actor">
      <FontAwesomeIcon icon={view.icon} aria-hidden="true" />
      <span>
        {view.before}
        <b>{view.who}</b>
      </span>
    </span>
  );
}

export function EstadoCell({ item }) {
  return (
    <>
      <IpcStatusChip item={item} />
      <div className="crm-rollo__s-row">
        <Actor estado={item.estado} />
      </div>
    </>
  );
}

export function ManualMark({ long = false }) {
  return (
    <span className="crm-ipc__manual" title="El sistema ya no la recalcula">
      <FontAwesomeIcon icon={faThumbtack} aria-hidden="true" />
      {long ? "Prioridad fijada a mano" : "Fijada a mano"}
    </span>
  );
}

export function PrioridadCell({ item }) {
  if (!item.prioridad) return <Muted />;
  return (
    <span className="crm-ipc__prio">
      <PriorityChip prioridad={item.prioridad} />
      {item.prioridadManual ? <ManualMark /> : null}
    </span>
  );
}

// "20/10/2026 16:40", or only the date when the value has no time.
export const dateTime = (value) => {
  const time = formatTime(value);
  return time ? `${formatDate(value)} ${time}` : formatDate(value);
};

// Next contact: overdue in the normal ink (no red, D24), today with its time, or the date and how far it is.
export function ProximoContactoCell({ item, today }) {
  if (!item.fechaProximoContacto) return <Muted />;
  const days = daysBetween(today, toIsoDate(item.fechaProximoContacto));

  if (item.contactoVencido) {
    return (
      <>
        <span className="crm-ipc__overdue">{`Vencido ${formatRelativeDays(-(days ?? 0))}`}</span>
        <span className="crm-rollo__s">{dateTime(item.fechaProximoContacto)}</span>
      </>
    );
  }

  if (days === 0) {
    const time = formatTime(item.fechaProximoContacto);
    return (
      <>
        <span className="crm-rollo__t">{time ? `Hoy ${time}` : "Hoy"}</span>
        <span className="crm-rollo__s">{formatDate(item.fechaProximoContacto)}</span>
      </>
    );
  }

  return (
    <>
      {dateTime(item.fechaProximoContacto)}
      {days > 0 ? <span className="crm-rollo__s">{days === 1 ? "en 1 día" : `en ${days} días`}</span> : null}
    </>
  );
}

export function ResponsableCell({ item, currentUserId }) {
  if (!item.responsableNombre && (item.responsableUsuarioId === null || item.responsableUsuarioId === undefined)) {
    return <Muted>Sin asignar</Muted>;
  }
  return (
    <>
      {item.responsableNombre || `Usuario ${item.responsableUsuarioId}`}
      {isMine(item, currentUserId) ? <span className="crm-rollo__s">Vos</span> : null}
    </>
  );
}

export function AntiguedadCell({ item }) {
  return (
    <>
      {formatRelativeDays(item.diasAbierta)}
      <span className="crm-rollo__s">{formatDate(item.fechaAlta)}</span>
      {item.demorada ? (
        <div className="crm-rollo__s-row">
          <LateTag title={item.fechaUltimaAccion ? `Sin ninguna acción desde el ${formatDate(item.fechaUltimaAccion)}` : undefined}>
            Demorada
          </LateTag>
        </div>
      ) : null}
    </>
  );
}

export const motivoCierreLabel = (item) => (item.motivoCierre ? labelFor(MOTIVO_CIERRE_LABELS, item.motivoCierre) : null);

// Result of a closed gestión with its closing motive; a histórico shows its original state and the "Histórico" chip.
export function ResultadoCell({ item }) {
  const motivoCierre = motivoCierreLabel(item);
  return (
    <>
      <div className="crm-rollo__s-row crm-ipc__result">
        <IpcStatusChip item={item} />
        {item.esHistorico ? <HistoricoChip /> : null}
      </div>
      {motivoCierre ? <span className="crm-rollo__s">{motivoCierre}</span> : null}
    </>
  );
}

export function FechaCierreCell({ item }) {
  return item.fechaCierre ? <>{formatDate(item.fechaCierre)}</> : <Muted />;
}

export function DuracionCell({ item }) {
  return item.fechaCierre ? <>{formatDuration(item.diasAbierta)}</> : <Muted />;
}
